/**
 * @name RolePermission.jsx
 * @description 역할/권한/메뉴 관리 페이지 (슈퍼관리자 전용)
 *
 * - 좌측: 역할 목록 (DB에서 동적 로드, 커스텀 역할 추가/삭제 가능)
 * - 우측 탭1: 해당 역할의 권한 체크박스 (카테고리별 그룹)
 * - 우측 탭2: 해당 역할의 메뉴 체크박스 (트리 구조)
 */
import React, { useState } from 'react';
import useRolePermission from '@hooks/comm/useRolePermission';
import useRoleManagement from '@hooks/comm/useRoleManagement';

/** 카테고리 한글 레이블 */
const CATEGORY_LABEL = {
    ACCIDENT:  '재해관리',
    AI:        'AI 알리미',
    CODE:      '공통코드',
    COMPANY:   '고객사 관리',
    DASHBOARD: '대시보드',
    DEPT:      '부서관리',
    EMPLOYEE:  '사원관리',
    MENU:      '메뉴관리',
    ROLE:      '역할관리',
    POS:       '직위관리',
    SITE:      '사업장관리',
};

const getCategoryLabel = (cat) => CATEGORY_LABEL[cat] ?? cat;

/**
 * 배타적 권한 그룹: 같은 배열 안에 있는 코드는 하나만 선택 가능
 * 선택 시 나머지는 자동 해제되고, UI는 라디오 버튼 스타일로 표시
 */
const EXCLUSIVE_GROUPS = [
    ['AI_SCOPE_ALL', 'AI_SCOPE_COMPANY', 'AI_SCOPE_PERSONAL'],
];

/** permCode가 속한 배타적 그룹을 반환 (없으면 null) */
const getExclusiveGroup = (permCode) =>
    EXCLUSIVE_GROUPS.find(g => g.includes(permCode)) ?? null;

/** 역할 추가 다이얼로그 */
const RoleFormDialog = ({ roleList, onConfirm, onCancel, isCreating }) => {
    const [displayName, setDisplayName] = useState('');
    const [baseRoleCode, setBaseRoleCode] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!displayName.trim()) return;
        onConfirm({ displayName: displayName.trim(), baseRoleCode: baseRoleCode || null });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-1">역할 추가</h3>
                <p className="text-sm text-gray-400 mb-5">새 역할을 만들고 기반 역할의 권한·메뉴를 복사할 수 있습니다.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-base font-medium text-gray-700 mb-1">역할 이름 <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="예: 현장관리자"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-base font-medium text-gray-700 mb-1">기반 역할 <span className="text-sm font-normal text-gray-400">(권한·메뉴 복사)</span></label>
                        <select
                            value={baseRoleCode}
                            onChange={(e) => setBaseRoleCode(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">빈 역할로 시작</option>
                            {roleList.map(r => (
                                <option key={r.roleCode} value={r.roleCode}>
                                    {r.displayName || r.roleCode}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={onCancel}
                            className="px-4 py-2 text-base text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                            취소
                        </button>
                        <button type="submit" disabled={isCreating || !displayName.trim()}
                            className="px-4 py-2 text-base font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                            {isCreating ? '생성 중...' : '역할 생성'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/** 권한 카테고리 카드 */
const PermCategoryCard = ({ category, perms, checkedPerms, onToggle, diffAdded = [], diffRemoved = [] }) => {
    const label = getCategoryLabel(category);

    // 카테고리 내 배타적 그룹에 속하는 permCode 집합
    const exclusiveCodes = new Set(
        EXCLUSIVE_GROUPS.flatMap(g =>
            perms.some(p => g.includes(p.permCode)) ? g : []
        )
    );

    // 배타적 권한 제외한 일반 권한만 카운트/전체선택 대상
    const normalPerms = perms.filter(p => !exclusiveCodes.has(p.permCode));

    const normalCheckedCount = normalPerms.filter(p => checkedPerms[p.permCode]).length;
    const allNormalChecked = normalPerms.length > 0 && normalCheckedCount === normalPerms.length;
    const totalCheckedCount = perms.filter(p => checkedPerms[p.permCode]).length;

    // 카테고리 내 diff 수
    const catAdded   = perms.filter(p => diffAdded.includes(p.permCode)).length;
    const catRemoved = perms.filter(p => diffRemoved.includes(p.permCode)).length;

    const toggleAll = () => {
        if (allNormalChecked) {
            normalPerms.forEach(p => checkedPerms[p.permCode] && onToggle(p.permCode));
        } else {
            normalPerms.forEach(p => !checkedPerms[p.permCode] && onToggle(p.permCode));
        }
    };

    // 배타적 그룹별로 묶기
    const exclusiveGroups = EXCLUSIVE_GROUPS
        .map(g => g.filter(code => exclusiveCodes.has(code) && perms.some(p => p.permCode === code)))
        .filter(g => g.length > 0);

    return (
        <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
            {/* 카테고리 헤더 */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-200">
                <div className="flex items-center gap-2">
                    <span className="text-base font-semibold text-gray-700">{label}</span>
                    <span className={`text-sm font-semibold px-1.5 py-0.5 rounded-full ${
                        totalCheckedCount > 0 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
                    }`}>
                        {totalCheckedCount}/{perms.length}
                    </span>
                    {catAdded > 0 && (
                        <span className="text-sm font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            +{catAdded}
                        </span>
                    )}
                    {catRemoved > 0 && (
                        <span className="text-sm font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">
                            -{catRemoved}
                        </span>
                    )}
                </div>
                {normalPerms.length > 0 && (
                    <button
                        onClick={toggleAll}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    >
                        {allNormalChecked ? '전체 해제' : '전체 선택'}
                    </button>
                )}
            </div>

            <div className="p-3 space-y-3">
                {/* 배타적 그룹: 라디오 스타일 */}
                {exclusiveGroups.map((group, gi) => {
                    const groupPerms = group.map(code => perms.find(p => p.permCode === code)).filter(Boolean);
                    return (
                        <div key={gi}>
                            <p className="text-sm text-gray-400 mb-1.5 px-1">※ 하나만 선택</p>
                            <div className="flex flex-wrap gap-1.5">
                                {groupPerms.map(p => {
                                    const checked = !!checkedPerms[p.permCode];
                                    const isAdded   = diffAdded.includes(p.permCode);
                                    const isRemoved = diffRemoved.includes(p.permCode);
                                    return (
                                        <button
                                            key={p.permCode}
                                            onClick={() => onToggle(p.permCode)}
                                            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-base font-medium transition-all select-none border ${
                                                isAdded
                                                    ? 'bg-emerald-500 text-white border-emerald-500 ring-2 ring-emerald-300'
                                                    : isRemoved
                                                        ? 'bg-red-50 text-red-500 border-red-300 line-through'
                                                        : checked
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : 'bg-white text-gray-500 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                                            }`}
                                        >
                                            <span className="text-xs">
                                                {isAdded ? '＋' : isRemoved ? '－' : checked ? '●' : '○'}
                                            </span>
                                            {p.permName}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {/* 구분선 */}
                {exclusiveGroups.length > 0 && normalPerms.length > 0 && (
                    <div className="border-t border-gray-200" />
                )}

                {/* 일반 권한: 체크박스 스타일 */}
                {normalPerms.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {normalPerms.map(p => {
                            const checked = !!checkedPerms[p.permCode];
                            const isAdded   = diffAdded.includes(p.permCode);
                            const isRemoved = diffRemoved.includes(p.permCode);
                            return (
                                <button
                                    key={p.permCode}
                                    onClick={() => onToggle(p.permCode)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-base font-medium transition-all select-none ${
                                        isAdded
                                            ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                                            : isRemoved
                                                ? 'bg-red-50 text-red-500 border border-red-300 line-through'
                                                : checked
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300 hover:text-blue-600'
                                    }`}
                                >
                                    <span className="text-sm">
                                        {isAdded ? '＋' : isRemoved ? '－' : checked ? '✓' : '○'}
                                    </span>
                                    {p.permName}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const RolePermission = () => {
    const [selectedRoleCode, setSelectedRoleCode] = useState('R0');
    const [activeTab, setActiveTab] = useState('perm'); // 'perm' | 'menu'
    const [checkedPerms, setCheckedPerms] = useState({});
    const [checkedMenus, setCheckedMenus] = useState({});
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [pendingRoleCode, setPendingRoleCode] = useState(null);

    // 서버 원본 상태 스냅샷 (diff 계산용, 렌더 불필요 → useRef)
    const originalPerms = React.useRef({});
    const originalMenus = React.useRef({});

    const {
        allPermissions,
        rolePermCodes,
        menuTree,
        roleMenuCodes,
        isLoading,
        savePerms,
        saveMenus,
        isSaving,
    } = useRolePermission(selectedRoleCode);

    const {
        roleList,
        isLoading: roleListLoading,
        createRole,
        deleteRole,
        isCreating,
        isDeleting,
    } = useRoleManagement();

    // 원본 대비 변경사항 계산 (ref는 deps 제외해도 안전)
    const permDiff = React.useMemo(() => {
        const added = [], removed = [];
        Object.keys(checkedPerms).forEach(code => {
            const now = !!checkedPerms[code];
            const orig = !!originalPerms.current[code];
            if (now && !orig) added.push(code);
            if (!now && orig) removed.push(code);
        });
        return { added, removed, hasChanges: added.length > 0 || removed.length > 0 };
    }, [checkedPerms]); // eslint-disable-line react-hooks/exhaustive-deps

    const menuDiff = React.useMemo(() => {
        const added = [], removed = [];
        Object.keys(checkedMenus).forEach(code => {
            const now = !!checkedMenus[code];
            const orig = !!originalMenus.current[code];
            if (now && !orig) added.push(code);
            if (!now && orig) removed.push(code);
        });
        return { added, removed, hasChanges: added.length > 0 || removed.length > 0 };
    }, [checkedMenus]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRoleSelect = (code) => {
        if (code === selectedRoleCode) return;
        if (permDiff.hasChanges || menuDiff.hasChanges) {
            setPendingRoleCode(code);
        } else {
            setSelectedRoleCode(code);
            setCheckedPerms({});
            setCheckedMenus({});
        }
    };

    const handleConfirmLeave = () => {
        setSelectedRoleCode(pendingRoleCode);
        setCheckedPerms({});
        setCheckedMenus({});
        setPendingRoleCode(null);
    };

    React.useEffect(() => {
        const map = {};
        rolePermCodes.forEach(c => { map[c] = true; });
        setCheckedPerms(map);
        originalPerms.current = { ...map };
    }, [rolePermCodes]);

    React.useEffect(() => {
        const map = {};
        roleMenuCodes.forEach(c => { map[c] = true; });
        setCheckedMenus(map);
        originalMenus.current = { ...map };
    }, [roleMenuCodes]);

    const togglePerm = (permCode) => {
        const group = getExclusiveGroup(permCode);
        if (group) {
            // 배타적 그룹: 선택한 것만 true, 나머지 그룹 항목은 false
            setCheckedPerms(prev => {
                const next = { ...prev };
                group.forEach(code => { next[code] = false; });
                next[permCode] = true;
                return next;
            });
        } else {
            setCheckedPerms(prev => ({ ...prev, [permCode]: !prev[permCode] }));
        }
    };

    // 메뉴 트리에서 특정 코드의 모든 하위 코드를 재귀 수집
    const collectDescendantCodes = (nodes, targetCode) => {
        for (const node of nodes) {
            if (node.menuCode === targetCode) {
                const codes = [];
                const collect = (children) => {
                    children?.forEach(child => {
                        codes.push(child.menuCode);
                        collect(child.children);
                    });
                };
                collect(node.children);
                return codes;
            }
            const found = collectDescendantCodes(node.children || [], targetCode);
            if (found !== null) return found;
        }
        return null;
    };

    // 특정 코드의 직속 부모 코드를 반환 (최상위면 null, 미발견 undefined)
    const findParentCode = (nodes, targetCode, parentCode = null) => {
        for (const node of nodes) {
            if (node.menuCode === targetCode) return parentCode;
            const found = findParentCode(node.children || [], targetCode, node.menuCode);
            if (found !== undefined) return found;
        }
        return undefined;
    };

    const toggleMenu = (menuCode) => {
        setCheckedMenus(prev => {
            const willCheck = !prev[menuCode];

            // 체크 시: 부모가 체크되지 않은 경우 무시
            if (willCheck) {
                const parentCode = findParentCode(menuTree, menuCode);
                if (parentCode !== null && parentCode !== undefined && !prev[parentCode]) {
                    return prev;
                }
            }

            const next = { ...prev, [menuCode]: willCheck };
            const descendants = collectDescendantCodes(menuTree, menuCode);

            if (!willCheck) {
                // 체크 해제: 모든 하위 메뉴 일괄 해제
                descendants?.forEach(code => { next[code] = false; });
            } else {
                // 체크: 모든 하위 메뉴 일괄 체크
                descendants?.forEach(code => { next[code] = true; });
            }

            return next;
        });
    };

    const handleSavePerms = () => {
        const permCodes = Object.entries(checkedPerms).filter(([, v]) => v).map(([k]) => k);
        savePerms(selectedRoleCode, permCodes);
    };

    const handleSaveMenus = () => {
        const menuCodes = Object.entries(checkedMenus).filter(([, v]) => v).map(([k]) => k);
        saveMenus(selectedRoleCode, menuCodes);
    };

    const handleCreateRole = (dto) => {
        createRole(dto);
        setShowAddDialog(false);
    };

    const handleDeleteRole = () => {
        if (!deleteTarget) return;
        deleteRole(deleteTarget);
        setDeleteTarget(null);
        if (selectedRoleCode === deleteTarget) setSelectedRoleCode('R0');
    };

    // 카테고리별 그룹화 + 카테고리 순서 정렬
    const permsByCategory = allPermissions.reduce((acc, p) => {
        if (!acc[p.category]) acc[p.category] = [];
        acc[p.category].push(p);
        return acc;
    }, {});

    // 전체 권한 선택 개수
    const totalChecked = Object.values(checkedPerms).filter(Boolean).length;
    const totalPerms = allPermissions.length;
    const totalMenuChecked = Object.values(checkedMenus).filter(Boolean).length;

    // 선택된 역할 표시명
    const selectedRole = roleList.find(r => r.roleCode === selectedRoleCode);
    const selectedRoleName = selectedRole?.displayName || selectedRoleCode;

    // 메뉴 트리 재귀 렌더링
    const renderMenuChildren = (nodes, depth = 1, parentChecked = true) =>
        nodes.map(menu => {
            const isChecked = !!checkedMenus[menu.menuCode];
            const isDisabled = !parentChecked;
            const isAdded   = !isDisabled && menuDiff.added.includes(menu.menuCode);
            const isRemoved = !isDisabled && menuDiff.removed.includes(menu.menuCode);
            const menuStyle = isDisabled
                ? { color: '#d1d5db', cursor: 'not-allowed' }
                : isAdded
                    ? { background: '#d1fae5', color: '#065f46', fontWeight: 600 }
                    : isRemoved
                        ? { background: '#fef2f2', color: '#dc2626', textDecoration: 'line-through' }
                        : isChecked
                            ? { background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }
                            : { color: '#6b7280' };
            return (
                <div key={menu.menuCode}>
                    <button
                        onClick={() => !isDisabled && toggleMenu(menu.menuCode)}
                        disabled={isDisabled}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-base transition-colors text-left"
                        style={menuStyle}
                    >
                        <span style={{ width: 16, textAlign: 'center', fontSize: 14 }}>
                            {isDisabled ? '—' : isAdded ? '＋' : isRemoved ? '－' : isChecked ? '✓' : '○'}
                        </span>
                        <span style={{ marginLeft: (depth - 1) * 8 }}>{menu.menuName}</span>
                        {menu.path && !isDisabled && (
                            <span className="ml-auto text-sm font-normal" style={{ opacity: 0.6 }}>{menu.path}</span>
                        )}
                    </button>
                    {menu.children?.length > 0 && (
                        <div className="ml-4">
                            {renderMenuChildren(menu.children, depth + 1, isChecked && parentChecked)}
                        </div>
                    )}
                </div>
            );
        });

    const renderMenuGrid = (roots) => (
        <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
            {roots.map(menu => {
                const childCount = menu.children?.length || 0;
                const checkedChildCount = menu.children?.filter(c => checkedMenus[c.menuCode]).length || 0;
                const isChecked = !!checkedMenus[menu.menuCode];
                const isAdded   = menuDiff.added.includes(menu.menuCode);
                const isRemoved = menuDiff.removed.includes(menu.menuCode);

                const cardStyle = isAdded
                    ? { borderColor: '#6ee7b7', background: '#ecfdf5' }
                    : isRemoved
                        ? { borderColor: '#fca5a5', background: '#fef2f2' }
                        : isChecked
                            ? { borderColor: '#bfdbfe', background: '#eff6ff' }
                            : { borderColor: '#e5e7eb', background: '#f9fafb' };

                const headerBorderColor = isAdded ? '#6ee7b7' : isRemoved ? '#fca5a5' : isChecked ? '#bfdbfe' : '#e5e7eb';

                return (
                    <div key={menu.menuCode}
                        className="rounded-xl border overflow-hidden"
                        style={cardStyle}
                    >
                        {/* 최상위 메뉴 헤더 */}
                        <button
                            onClick={() => toggleMenu(menu.menuCode)}
                            className="w-full flex items-center gap-2 px-4 py-3 text-left transition-colors"
                            style={{ borderBottom: '1px solid', borderColor: headerBorderColor }}
                        >
                            <span className="text-base" style={{
                                color: isAdded ? '#065f46' : isRemoved ? '#dc2626' : isChecked ? '#1d4ed8' : '#9ca3af'
                            }}>
                                {isAdded ? '＋' : isRemoved ? '－' : isChecked ? '✓' : '○'}
                            </span>
                            <span className="text-base font-bold" style={{
                                color: isAdded ? '#065f46' : isRemoved ? '#dc2626' : isChecked ? '#1d4ed8' : '#374151',
                                textDecoration: isRemoved ? 'line-through' : 'none',
                            }}>
                                {menu.menuName}
                            </span>
                            {childCount > 0 && (
                                <span className="ml-auto text-sm px-1.5 py-0.5 rounded-full font-medium"
                                    style={{
                                        background: checkedChildCount > 0 ? '#2563eb' : '#e5e7eb',
                                        color: checkedChildCount > 0 ? '#fff' : '#9ca3af'
                                    }}
                                >
                                    {checkedChildCount}/{childCount}
                                </span>
                            )}
                        </button>
                        {/* 하위 메뉴 */}
                        {childCount > 0 && (
                            <div className="p-2 space-y-0.5">
                                {renderMenuChildren(menu.children, 1, isChecked)}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">역할/권한 관리</h2>

            <div className="flex gap-6">
                {/* ── 좌측: 역할 목록 ─────────────────────────── */}
                <div className="w-52 flex-shrink-0">
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <span className="text-base font-semibold text-gray-600">역할 목록</span>
                            <button
                                onClick={() => setShowAddDialog(true)}
                                className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                + 추가
                            </button>
                        </div>

                        {roleListLoading ? (
                            <div className="px-4 py-4 text-base text-gray-400 text-center">로딩 중...</div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {roleList.map(role => {
                                    const isSelected = selectedRoleCode === role.roleCode;
                                    return (
                                        <div
                                            key={role.roleCode}
                                            className={`flex items-center transition-colors border-l-2 ${
                                                isSelected
                                                    ? 'bg-blue-50 border-blue-600'
                                                    : 'border-transparent hover:bg-gray-50'
                                            }`}
                                        >
                                            <button
                                                onClick={() => handleRoleSelect(role.roleCode)}
                                                className="flex-1 text-left px-4 py-3"
                                            >
                                                <div className={`text-base font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                                                    {role.displayName || role.roleCode}
                                                </div>
                                                {role.isSystem === 'N' && (
                                                    <div className="text-sm text-gray-400 mt-0.5">{role.roleCode}</div>
                                                )}
                                            </button>
                                            {role.isSystem === 'N' && (
                                                <button
                                                    onClick={() => setDeleteTarget(role.roleCode)}
                                                    className="px-3 py-3 text-gray-300 hover:text-red-400 transition-colors text-sm"
                                                    title="역할 삭제"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── 우측: 권한/메뉴 탭 ──────────────────────── */}
                <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* 탭 헤더 */}
                    <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200">
                        {/* 세그먼트 탭 */}
                        <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-1">
                            {[
                                {
                                    key: 'perm',
                                    label: '권한 설정',
                                    count: permDiff.hasChanges
                                        ? `+${permDiff.added.length}/-${permDiff.removed.length}`
                                        : `${totalChecked}/${totalPerms}`,
                                    hasChanges: permDiff.hasChanges,
                                },
                                {
                                    key: 'menu',
                                    label: '메뉴 설정',
                                    count: menuDiff.hasChanges
                                        ? `+${menuDiff.added.length}/-${menuDiff.removed.length}`
                                        : `${totalMenuChecked}개`,
                                    hasChanges: menuDiff.hasChanges,
                                },
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-base font-medium transition-all ${
                                        activeTab === tab.key
                                            ? 'bg-white text-gray-800 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {tab.label}
                                    <span className={`text-sm px-1.5 py-0.5 rounded-full font-semibold ${
                                        tab.hasChanges
                                            ? 'bg-amber-500 text-white'
                                            : activeTab === tab.key
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-300 text-gray-500'
                                    }`}>
                                        {tab.count}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* 역할명 + 저장 버튼 */}
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-400 hidden sm:block">
                                <span className="font-semibold text-gray-700">{selectedRoleName}</span> 역할
                            </span>
                            <button
                                onClick={activeTab === 'perm' ? handleSavePerms : handleSaveMenus}
                                disabled={isSaving}
                                className="relative px-5 py-2 bg-blue-600 text-white text-base font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                                {isSaving ? '저장 중...' : activeTab === 'perm' ? '권한 저장' : '메뉴 저장'}
                                {activeTab === 'perm' && permDiff.hasChanges && !isSaving && (
                                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-amber-400 text-white text-[10px] font-bold rounded-full leading-none">
                                        {permDiff.added.length + permDiff.removed.length}
                                    </span>
                                )}
                                {activeTab === 'menu' && menuDiff.hasChanges && !isSaving && (
                                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-amber-400 text-white text-[10px] font-bold rounded-full leading-none">
                                        {menuDiff.added.length + menuDiff.removed.length}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* 탭 콘텐츠 */}
                    <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 240px)' }}>
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16 text-gray-400">
                                <span className="text-base">로딩 중...</span>
                            </div>
                        ) : activeTab === 'perm' ? (
                            /* ── 권한 탭 ── */
                            <div className="grid grid-cols-2 gap-4">
                                {Object.entries(permsByCategory).map(([category, perms]) => (
                                    <PermCategoryCard
                                        key={category}
                                        category={category}
                                        perms={perms}
                                        checkedPerms={checkedPerms}
                                        onToggle={togglePerm}
                                        diffAdded={permDiff.added}
                                        diffRemoved={permDiff.removed}
                                    />
                                ))}
                                {Object.keys(permsByCategory).length === 0 && (
                                    <div className="text-center py-12 text-gray-400 text-sm">
                                        등록된 권한이 없습니다.
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* ── 메뉴 탭 ── */
                            <div>
                                {renderMenuGrid(menuTree)}
                                {menuTree.length === 0 && (
                                    <div className="text-center py-12 text-gray-400 text-sm">
                                        등록된 메뉴가 없습니다.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── 역할 추가 다이얼로그 ── */}
            {showAddDialog && (
                <RoleFormDialog
                    roleList={roleList}
                    onConfirm={handleCreateRole}
                    onCancel={() => setShowAddDialog(false)}
                    isCreating={isCreating}
                />
            )}

            {/* ── 미저장 변경사항 경고 다이얼로그 ── */}
            {pendingRoleCode && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-1 h-1 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-lg font-bold">!</div>
                            <h3 className="text-lg font-bold text-gray-800">저장하지 않은 변경사항</h3>
                        </div>
                        <p className="text-base text-gray-600 mb-1">
                            현재 역할에 저장하지 않은 변경사항이 있습니다.
                        </p>
                        <p className="text-sm text-gray-400 mb-5">
                            다른 역할로 이동하면 변경사항이 사라집니다. 계속하시겠습니까?
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setPendingRoleCode(null)}
                                className="px-4 py-2 text-base text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleConfirmLeave}
                                className="px-4 py-2 text-base font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-600"
                            >
                                이동
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── 역할 삭제 확인 다이얼로그 ── */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-1 h-1 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-lg font-bold">!</div>
                            <h3 className="text-lg font-bold text-gray-800">역할 삭제</h3>
                        </div>
                        <p className="text-base text-gray-600 mb-2">
                            <span className="font-semibold text-red-600">{roleList.find(r => r.roleCode === deleteTarget)?.displayName || deleteTarget}</span> 역할을 삭제합니다.
                        </p>
                        <p className="text-sm text-gray-400 mb-5">해당 역할의 권한·메뉴 매핑도 함께 삭제되며, 이 역할을 가진 사용자가 있으면 삭제할 수 없습니다.</p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                className="px-4 py-2 text-base text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleDeleteRole}
                                disabled={isDeleting}
                                className="px-4 py-2 text-base font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                                {isDeleting ? '삭제 중...' : '삭제 확인'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RolePermission;
